import { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { groupChatService } from '@/services/groupChatService'
import { toast } from 'sonner'
import { Loader2, UserPlus, Search } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useTranslation } from 'react-i18next'

interface AddParticipantDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    conversationId: string
}

/**
 * AddParticipantDialog Component
 * Allows admins/moderators to search for users and add them to the group
 */
export default function AddParticipantDialog({
    open,
    onOpenChange,
    conversationId,
}: AddParticipantDialogProps) {
    const { t } = useTranslation('common')
    const queryClient = useQueryClient()
    // Removed useChat hook to prevent duplicate socket connections
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

    // Search users query
    const { data: searchResults = [], isLoading: isSearching } = useQuery({
        queryKey: ['userSearch', searchQuery],
        queryFn: async () => {
            if (!searchQuery || searchQuery.trim().length < 2) {
                return []
            }
            const results = await groupChatService.searchUsers(searchQuery)
            return results
        },
        enabled: searchQuery.trim().length >= 2,
    })

    // Add participant mutation
    const addParticipantMutation = useMutation({
        mutationFn: (userId: string) =>
            groupChatService.addParticipant(conversationId, { userId }),
        onSuccess: () => {
            toast.success(t('chat.addParticipant.success'))
            // Refresh participants list
            queryClient.invalidateQueries({ queryKey: ['groupParticipants', conversationId] })
            // Reset form
            setSearchQuery('')
            setSelectedUserId(null)
            onOpenChange(false)
        },
        onError: (error: any) => {
            toast.error(
                error.response?.data?.message || t('chat.addParticipant.errors.addFailed')
            )
        },
    })

    const handleAddParticipant = () => {
        if (!selectedUserId) {
            toast.error(t('chat.addParticipant.selectUser'))
            return
        }
        addParticipantMutation.mutate(selectedUserId)
    }

    const getAvatarUrl = (user: any) => {
        if (!user) return ''
        if (user.avatar) {
            return user.avatar.startsWith('http')
                ? user.avatar
                : `${import.meta.env.VITE_CDN_BASE_URL}${user.avatar}`
        }
        const firstName = user.firstname || user.firstName || ''
        const lastName = user.lastname || user.lastName || ''
        const fullName = `${firstName} ${lastName}`.trim()
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(
            user.displayName || fullName || user.username || 'User'
        )}&size=128&background=random`
    }

    const getInitials = (user: any) => {
        if (!user) return '??'
        const firstName = user.firstname || user.firstName || ''
        const lastName = user.lastname || user.lastName || ''
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '??'
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('chat.addParticipant.title')}</DialogTitle>
                    <DialogDescription>
                        {t('chat.addParticipant.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Search input */}
                    <div className="space-y-2">
                        <Label htmlFor="search">{t('chat.addParticipant.searchLabel')}</Label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                id="search"
                                type="text"
                                placeholder={t('chat.addParticipant.searchPlaceholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Search results */}
                    <div className="space-y-2">
                        {isSearching ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : searchResults.length > 0 ? (
                            <div className="max-h-[300px] overflow-y-auto space-y-2">
                                {searchResults.map((user: any) => (
                                    <div
                                        key={user.id}
                                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedUserId === user.id
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:bg-gray-50'
                                            }`}
                                        onClick={() => setSelectedUserId(user.id)}
                                    >
                                        <Avatar className="h-10 w-10">
                                            <AvatarImage src={getAvatarUrl(user)} />
                                            <AvatarFallback>{getInitials(user)}</AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm">
                                                {user.firstname} {user.lastname}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {user.role}
                                            </p>
                                        </div>
                                        {selectedUserId === user.id && (
                                            <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                                                <svg
                                                    className="h-3 w-3 text-white"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : searchQuery.trim().length >= 2 ? (
                            <p className="text-sm text-gray-500 text-center py-8">
                                {t('chat.addParticipant.noUsersFound')}
                            </p>
                        ) : (
                            <p className="text-sm text-gray-500 text-center py-8">
                                {t('chat.addParticipant.typeToSearch')}
                            </p>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                        >
                            {t('chat.addParticipant.cancel')}
                        </Button>
                        <Button
                            onClick={handleAddParticipant}
                            disabled={!selectedUserId || addParticipantMutation.isPending}
                        >
                            {addParticipantMutation.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            <UserPlus className="mr-2 h-4 w-4" />
                            {t('chat.addParticipant.addToGroup')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
